import { AsyncProgressStatus, ImportTypeConfig } from "domains/stock-transfer-imports/types";
import { Button, CircularProgress, Stack, Typography } from "@mui/material";
import {
    FileHttpErrorPane,
    FileImportInvalidPane,
    FileImportValidPane,
    ImportResults,
    ImportWarning
} from "domains/stock-transfer-imports/components";
import { FileNameConflictResponse, HttpError } from "components/ServerValidationErrors";
import { useEffect, useState } from "react";

import FactCheckIcon from "@mui/icons-material/FactCheck";
import localisations from "../../../localisation";
import { usePerformStockTransferFileImport } from "domains/stock-transfer-imports/services/use-stock-transfer-import-api";

type ConfirmationStageProps = {
    fieldCompanyId: number;
    importStatus: AsyncProgressStatus;
    handleImportStatusChange: (status: AsyncProgressStatus) => void;
    importFile: File | undefined;
    importTypeConfig: ImportTypeConfig | undefined;
    forceUploadChoice: boolean;
    handleFileNameConflict: (status: AsyncProgressStatus, error: FileNameConflictResponse) => void;
};

function ConfirmationStage(props: ConfirmationStageProps) {
    const { heading, blurb, startImportButton } = localisations.stockTransferImport.stages.confirmation;
    const [localFile, setLocalFile] = useState<File | undefined>(undefined);
    const { data, error, status } = usePerformStockTransferFileImport(
        props.fieldCompanyId,
        localFile,
        props.importTypeConfig,
        props.forceUploadChoice
    );
    const { handleImportStatusChange, handleFileNameConflict } = props;

    useEffect(() => {
        handleImportStatusChange(status);
    }, [handleImportStatusChange, status]);

    useEffect(() => {
        if (status !== AsyncProgressStatus.Success) {
            resetFileStateForNextSubmission();
            handleFileNameConflict(status, error as FileNameConflictResponse);
        }
    }, [status, error, handleFileNameConflict]);

    function resetFileStateForNextSubmission() {
        setLocalFile(undefined);
    }

    const handleSubmit = async () => {
        setLocalFile(props.importFile);
    };

    const isGeneralError = (error: Error | null | FileNameConflictResponse) =>
        error && (!(error instanceof HttpError) || (error as HttpError).status >= 500);

    return (
        <>
            <Typography variant="h5" gutterBottom>
                {heading}
            </Typography>

            <Stack spacing={1}>
                {props.importStatus === AsyncProgressStatus.NotSubmitted && (
                    <>
                        <Typography variant="body1" gutterBottom>
                            {blurb}
                        </Typography>
                        <Button variant="contained" endIcon={<FactCheckIcon />} onClick={() => handleSubmit()}>
                            {startImportButton}
                        </Button>
                    </>
                )}
                {props.importStatus === AsyncProgressStatus.InProgress && 
                    <>
                        <ImportWarning />
                        <Loading />
                    </>
                 }
                {props.importStatus === AsyncProgressStatus.Success && <FileImportValidPane />}
                {props.importStatus === AsyncProgressStatus.Failed &&
                    (error instanceof HttpError &&  error?.message !== "Conflict" && isGeneralError(error) ? (
                        <FileHttpErrorPane errorMessage={error?.message} />
                    ) : (
                        <FileImportInvalidPane errorGuid={data?.requestId} />
                    ))}
                {(props.importStatus === AsyncProgressStatus.Failed ||
                    props.importStatus === AsyncProgressStatus.Success) && (
                    <ImportResults
                        requestErrors={data ? data.requestErrors : (error as any)?.data?.requestErrors}
                        itemErrors={data ? data.requestItemErrors : (error as any)?.data?.requestItemErrors}
                        itemActions={data ? data.requestItemActions : (error as any)?.data?.requestItemActions}
                    />
                )}
            </Stack>
        </>
    );
}

function Loading() {
    return (
        <Stack spacing={5} alignItems="center">
            <CircularProgress aria-busy="true" />
        </Stack>
    );
}

export default ConfirmationStage;
